import React from 'react';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import {
  formatSupplierType,
  formatIndustrySector,
  formatEmployeeCount,
  formatAnnualTurnover,
  formatMsmeStatus
} from '../../../utils/formatters';
import { isIndia } from '../../../utils/countries';

const OverviewTab = ({ vendor }) => {
  // For new vendors, show empty/zero values instead of fake data
  const summaryCards = [
    {
      title: 'Total Orders',
      value: '0',
      change: '0%',
      changeType: 'neutral',
      icon: 'ShoppingCart'
    },
    {
      title: 'Total Value',
      value: '₹0',
      change: '0%',
      changeType: 'neutral',
      icon: 'IndianRupee'
    },
    {
      title: 'Avg. Rating',
      value: 'Coming Soon',
      change: '',
      changeType: 'neutral',
      icon: 'Star'
    },
    {
      title: 'Response Time',
      value: 'Coming Soon',
      change: '',
      changeType: 'neutral',
      icon: 'Clock'
    }
  ];

  // Empty activities for new vendors
  const recentActivities = [];

  const getChangeColor = (changeType) => {
    switch (changeType) {
      case 'positive':
        return 'text-emerald-700';
      case 'negative':
        return 'text-red-700';
      default:
        return 'text-slate-600';
    }
  };

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card, index) => {
          const iconColors = [
            'bg-blue-100 text-blue-600',
            'bg-emerald-100 text-emerald-600',
            'bg-amber-100 text-amber-600',
            'bg-indigo-100 text-indigo-600'
          ];
          
          return (
            <Card key={index} className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all duration-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 ${iconColors[index % 4]} rounded-lg flex items-center justify-center`}>
                    <Icon name={card.icon} size={20} />
                  </div>
                  {card.change && (
                    <span className={`text-xs font-medium ${getChangeColor(card.changeType)}`}>
                      {card.change}
                    </span>
                  )}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">{card.value}</h3>
                  <p className="text-sm font-medium text-slate-600">{card.title}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Info Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Company Information */}
        <Card className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all duration-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="Building2" size={20} className="text-blue-600" />
              </div>
              Company Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Annual Revenue:</span>
                <span className="font-medium text-slate-900">
                  {formatAnnualTurnover(vendor?.annual_turnover)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">MSME Status:</span>
                <span className="font-medium text-slate-900">{formatMsmeStatus(vendor?.msme_status)}</span>
              </div>
              {isIndia(vendor?.country_origin) ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-600">PAN Number:</span>
                    <span className="font-medium text-slate-900">{vendor?.pan_number || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">GST Number:</span>
                    <span className="font-medium text-slate-900">{vendor?.gst_number || 'N/A'}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Tax Registration Number:</span>
                    <span className="font-medium text-slate-900">{vendor?.tax_registration_number || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">VAT/Sales Tax Number:</span>
                    <span className="font-medium text-slate-900">{vendor?.vat_number || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Business License Number:</span>
                    <span className="font-medium text-slate-900">{vendor?.business_license || 'N/A'}</span>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="border border-slate-200 shadow-sm bg-white hover:shadow-md transition-all duration-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Icon name="User" size={20} className="text-emerald-600" />
              </div>
              Primary Contact
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Contact Person:</span>
                <span className="font-medium text-slate-900">{vendor?.contact_person_name || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Designation:</span>
                <span className="font-medium text-slate-900">{vendor?.designation || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Email:</span>
                <span className="font-medium text-slate-900">{vendor?.email || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Phone:</span>
                <span className="font-medium text-slate-900">{vendor?.phone_number || 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {/* <div className="bg-surface border border-border rounded-lg p-6 shadow-subtle">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Activity" size={20} className="mr-2" />
          Recent Activity
        </h3>
        {recentActivities.length > 0 ? (
          <div className="space-y-4">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-4 p-4 bg-muted rounded-lg">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(activity.status)} bg-current/10`}>
                  <Icon name={activity.icon} size={18} className={getStatusColor(activity.status)} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-foreground">{activity.title}</h4>
                  <p className="text-sm text-text-secondary mt-1">{activity.description}</p>
                  <span className="text-xs text-text-secondary">{activity.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Icon name="Activity" size={48} className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary">No recent activity</p>
            <p className="text-sm text-text-secondary mt-1">Activity will appear here once the vendor starts transacting</p>
          </div>
        )}
      </div> */}
    </div>
  );
};

export default OverviewTab;